/**
 * Product Addons - Gift & Card Picker
 * Handles mandatory selection and cart integration
 *
 * Le snippet product-addon-picker.liquid est rendu plusieurs fois sur la
 * page produit (cadeau + carte), ce qui charge ce script plusieurs fois.
 * Le guard ci-dessous empêche la redéclaration de la classe AddonPicker.
 */
if (!window.__productAddonsLoaded) {
  window.__productAddonsLoaded = true;

class AddonPicker extends HTMLElement {
    constructor() {
        super();
        this.type = this.dataset.type;
        this.required = this.dataset.required === 'true';
        this.selectedVariantId = null;
        this.selectedPrice = 0;
        this.errorElement = this.querySelector('.addon-picker__error');
        this.grid = this.querySelector('.addon-picker__grid');
    }

    connectedCallback() {
        this.setupListeners();
        this.registerWithForm();
    }

    setupListeners() {
        const inputs = this.querySelectorAll('input[type="radio"]');
        inputs.forEach(input => {
            input.addEventListener('change', this.handleSelection.bind(this));
        });
    }

    handleSelection(event) {
        const input = event.target;
        const item = input.closest('.addon-picker__item');

        this.selectedVariantId = input.value;
        this.selectedPrice = item.dataset.price ? parseInt(item.dataset.price, 10) : 0;

        // Clear error state
        this.hideError();

        // Dispatch custom event for other components
        this.dispatchEvent(new CustomEvent('addon:selected', {
            bubbles: true,
            detail: {
                type: this.type,
                variantId: this.selectedVariantId,
                price: this.selectedPrice
            }
        }));
    }

    validate() {
        if (this.required && !this.selectedVariantId) {
            this.showError();
            return false;
        }
        return true;
    }

    showError() {
        this.classList.add('has-error');
        if (this.errorElement) {
            this.errorElement.hidden = false;
        }
        // Scroll into view if not visible
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    hideError() {
        this.classList.remove('has-error');
        if (this.errorElement) {
            this.errorElement.hidden = true;
        }
    }

    getSelectedVariantId() {
        return this.selectedVariantId === 'none' ? null : this.selectedVariantId;
    }

    registerWithForm() {
        // Find the product form
        const productInfo = this.closest('product-info');
        if (productInfo) {
            const formId = productInfo.getAttribute('form');
            const form = document.getElementById(formId);
            if (form) {
                // Store reference on form for validation
                if (!form.addonPickers) {
                    form.addonPickers = [];
                    this.interceptFormSubmit(form);
                }
                form.addonPickers.push(this);
            }
        }
    }

    interceptFormSubmit(form) {
        // Find the product-form custom element
        const productForm = form.closest('product-form') || form;

        // Store original submit handler
        const originalSubmit = productForm.onSubmit?.bind(productForm);

        // Override the form submission
        form.addEventListener('submit', async (event) => {
            // Validate all addon pickers
            let allValid = true;
            const addonsToAdd = [];

            form.addonPickers.forEach(picker => {
                if (!picker.validate()) {
                    allValid = false;
                } else {
                    const variantId = picker.getSelectedVariantId();
                    if (variantId) {
                        addonsToAdd.push({
                            id: parseInt(variantId, 10),
                            quantity: 1
                        });
                    }
                }
            });

            // Croissant addons will be handled after cart check (see below)

            if (!allValid) {
                event.preventDefault();
                event.stopPropagation();
                return false;
            }

            // If we have addons to add, add them along with the main product
            if (addonsToAdd.length > 0) {
                event.preventDefault();
                event.stopPropagation();

                // Mark as submitting
                form.addonPickers.forEach(p => p.setAttribute('data-submitting', 'true'));

                try {
                    // Get the main product variant ID
                    const mainVariantInput = form.querySelector('input[name="id"]');
                    const quantityInput = form.querySelector('input[name="quantity"]');
                    const mainVariantId = mainVariantInput?.value;
                    const quantity = quantityInput?.value || 1;

                    if (mainVariantId) {
                        // Fetch current cart to check for existing free croissant
                        const cartResponse = await fetch('/cart.js');
                        const cart = await cartResponse.json();

                        // Check if a free croissant already exists in cart
                        const hasFreeCroissant = cart.items.some(item =>
                            item.properties && item.properties['_croissant_type'] === 'offert'
                        );

                        // Collect croissant addons, filtering out free one if already in cart
                        if (form.croissantAddons) {
                            form.croissantAddons.forEach(croissant => {
                                const items = croissant.getItemsToAdd();
                                if (items && items.length > 0) {
                                    items.forEach(item => {
                                        // Skip free croissant if one already exists in cart
                                        if (item.properties?.['_croissant_type'] === 'offert' && hasFreeCroissant) {
                                            return; // Skip this item
                                        }
                                        addonsToAdd.push(item);
                                    });
                                }
                            });
                        }

                        // Prepare items array with main product + addons
                        const items = [
                            {
                                id: parseInt(mainVariantId, 10),
                                quantity: parseInt(quantity, 10)
                            },
                            ...addonsToAdd
                        ];

                        // Add all items to cart
                        const response = await fetch('/cart/add.js', {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                                'Accept': 'application/json'
                            },
                            body: JSON.stringify({ items })
                        });

                        if (!response.ok) {
                            let parsed;
                            try { parsed = await response.json(); } catch (_) {}
                            const userMessage = parsed?.description || parsed?.message || `Erreur ${response.status}`;
                            throw new Error(userMessage);
                        }

                        // Trigger cart update events
                        document.dispatchEvent(new CustomEvent('cart:refresh'));

                        // Open cart drawer if available
                        const cartDrawer = document.getElementById('CartDrawer');
                        if (cartDrawer) {
                            cartDrawer.removeAttribute('hidden');
                            cartDrawer.setAttribute('open', '');
                            // Also dispatch the standard Shopify event
                            document.dispatchEvent(new CustomEvent('cart:item-added'));
                        }

                        // Fetch and update cart drawer content
                        this.updateCartDrawer();

                    }
                } catch (error) {
                    console.error('Error adding to cart:', error);
                    alert(error?.message ? `Impossible d'ajouter au panier : ${error.message}` : 'Une erreur s\'est produite. Veuillez réessayer.');
                } finally {
                    form.addonPickers.forEach(p => p.removeAttribute('data-submitting'));
                }
            }
        });
    }

    async updateCartDrawer() {
        try {
            // Fetch updated cart drawer section
            const response = await fetch('/?sections=cart-drawer');
            if (response.ok) {
                const data = await response.json();
                const cartDrawerContent = data['cart-drawer'];

                if (cartDrawerContent) {
                    // Find the cart drawer and update it
                    const cartDrawerSection = document.querySelector('#shopify-section-cart-drawer');
                    if (cartDrawerSection) {
                        cartDrawerSection.innerHTML = cartDrawerContent;
                    }

                    // Re-initialize cart drawer
                    const cartDrawer = document.getElementById('CartDrawer');
                    if (cartDrawer) {
                        cartDrawer.removeAttribute('hidden');
                    }
                }
            }
        } catch (error) {
            console.error('Error updating cart drawer:', error);
            // Fallback: reload page
            window.location.reload();
        }
    }
}

// Register custom element
if (!customElements.get('addon-picker')) {
    customElements.define('addon-picker', AddonPicker);
}

}
